import { css } from '@styled-system/css';
import { Radar, Radio } from 'lucide-react';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/shared/components/ui/Button';
import { Spinner } from '@/shared/components/ui/Spinner';

// ============================================================================
// États d'attente du feed de veille. Le premier chargement peut être long
// (lecture bornée à 12 s côté serveur, périmètres larges, veilles à
// similarité lues par tranches) : des SQUELETTES de cartes + un message qui
// dit pourquoi, plutôt qu'un spinner muet ou un faux « aucune vente ».
// ============================================================================

const wrap = css({ display: 'flex', flexDirection: 'column', gap: '4' });

const notice = css({
  display: 'flex',
  alignItems: 'center',
  gap: '3',
  fontSize: 'caption',
  color: 'text.tertiary',
  paddingX: '1',
});

const skeletonCard = css({
  display: 'flex',
  gap: '5',
  padding: '5',
  bg: 'surface.default',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'border.subtle',
  borderRadius: 'xl',
  maxWidth: '900px',
  animation: 'skeletonPulse 1.6s ease-in-out infinite',
});

const skeletonImage = css({
  width: '140px',
  height: '150px',
  borderRadius: 'lg',
  bg: 'surface.sunken',
  flexShrink: 0,
});

const skeletonBody = css({ display: 'flex', flexDirection: 'column', gap: '3', flex: 1, paddingY: '2' });

const bar = css({ height: '12px', borderRadius: 'full', bg: 'surface.sunken' });

const SkeletonCard: FC<{ delay: number }> = ({ delay }) => (
  <div className={skeletonCard} style={{ animationDelay: `${delay}ms` }}>
    <div className={skeletonImage} />
    <div className={skeletonBody}>
      <div className={bar} style={{ width: '55%' }} />
      <div className={bar} style={{ width: '30%' }} />
      <div className={bar} style={{ width: '20%', height: '16px' }} />
      <div className={css({ flex: 1 })} />
      <div className={bar} style={{ width: '40%' }} />
    </div>
  </div>
);

/** Premier chargement : squelettes + explication (périmètre large = long). */
export const VeilleFeedSkeleton: FC = () => {
  const { t } = useTranslation();
  return (
    <div className={wrap}>
      <span className={notice}>
        <Spinner size={13} />
        {t('veille.feedLoadingHint')}
      </span>
      {[0, 180, 360].map((delay) => (
        <SkeletonCard key={delay} delay={delay} />
      ))}
    </div>
  );
};

/**
 * Balayage par tranches en cours (pages vides avec curseur, budget serveur) :
 * la fenêtre de 6 h est parcourue morceau par morceau, rien à montrer ENCORE
 * — surtout ne pas afficher « aucune vente ».
 */
export const VeilleFeedScanning: FC = () => {
  const { t } = useTranslation();
  return (
    <div
      className={css({
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '4',
        paddingY: '14',
        color: 'text.tertiary',
        textAlign: 'center',
      })}
    >
      <span className={css({ position: 'relative', display: 'inline-flex' })}>
        <Radar size={30} strokeWidth={1.25} className={css({ animation: 'skeletonPulse 1.6s ease-in-out infinite' })} />
      </span>
      <span className={css({ fontSize: 'bodySm', fontWeight: 'medium', color: 'text.secondary' })}>
        {t('veille.feedScanningTitle')}
      </span>
      <span className={css({ fontSize: 'caption', maxWidth: '420px', lineHeight: 1.6 })}>
        {t('veille.feedScanningHint')}
      </span>
    </div>
  );
};

/**
 * Backfill des 7 jours d'historique en cours (historyState queued/running) :
 * l'onglet Statistiques n'a RIEN à montrer encore — gros état d'attente
 * explicite plutôt que des graphes vides sous un bandeau discret.
 */
export const VeilleHistoryPending: FC<{ onGoLive?: () => void }> = ({ onGoLive }) => {
  const { t } = useTranslation();
  return (
    <div
      className={css({
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '5',
        paddingY: '20',
        paddingX: '6',
        textAlign: 'center',
      })}
    >
      <span
        className={css({
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '56px',
          height: '56px',
          borderRadius: 'full',
          bg: 'surface.subtle',
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: 'border.default',
          color: 'text.secondary',
        })}
      >
        <Spinner size={22} />
      </span>
      <span className={css({ fontSize: 'lg', fontWeight: 'semibold', color: 'text.primary' })}>
        {t('veille.historyPendingTitle')}
      </span>
      <span className={css({ fontSize: 'bodySm', color: 'text.tertiary', maxWidth: '460px', lineHeight: 1.6 })}>
        {t('veille.historyPendingHint')}
      </span>
      {onGoLive && (
        <Button variant="secondary" size="md" icon={<Radio size={14} strokeWidth={1.75} />} onClick={onGoLive}>
          {t('veille.goLive')}
        </Button>
      )}
    </div>
  );
};
